/// Passage-planning framework — the structured RYA workflow every
/// Day Skipper + Coastal Skipper passage starts from. Builds on the
/// 4 P's mnemonic (Plan · People · Plot · Provisions) with concrete
/// stage-by-stage detail.
///
/// Three domains:
///
///   1. The full workflow — 8 stages from concept through arrival,
///      each with goals, inputs, outputs, and the questions you
///      should be able to answer at the end of that stage.
///   2. Tidal gate calculation — the canonical "must arrive between
///      HW-3 and HW+1" style of constraint that drives the timing.
///   3. Contingency planning — the bolt-holes / alternative ports
///      every passage plan should name in advance.

// ───────────────────────────────────────────────────────────────────
// 1. Workflow stages
// ───────────────────────────────────────────────────────────────────

export type WorkflowStageId =
  | "concept"
  | "weather-window"
  | "tidal-gates"
  | "route-design"
  | "contingencies"
  | "provisioning"
  | "crew-brief"
  | "departure-arrival";

export interface WorkflowStage {
  id: WorkflowStageId;
  /// Stage number — 1..8.
  step: number;
  name: string;
  /// One-line gist.
  summary: string;
  /// What you need IN to complete this stage.
  inputs: readonly string[];
  /// What this stage PRODUCES — the outputs the next stage consumes.
  outputs: readonly string[];
  /// Questions the skipper should be able to answer at the END of
  /// this stage.
  questions: readonly string[];
  /// When in the timeline this stage typically happens.
  timing: string;
}

export const WORKFLOW: readonly WorkflowStage[] = Object.freeze([
  {
    id: "concept",
    step: 1,
    name: "Concept + scope",
    summary:
      "Establish what kind of passage this is — day-sail, overnight, multi-day offshore — and the broad targets for departure + arrival.",
    inputs: [
      "Cruising intent (the WHY)",
      "Crew availability + skill level",
      "Boat capability + condition",
    ],
    outputs: [
      "Passage profile: type, distance, expected duration band, daylight vs darkness ratio",
      "Approximate departure date / time window",
    ],
    questions: [
      "Is this a coastal hop, overnight passage, or offshore leg?",
      "Day or night sailing expected?",
      "Crew + boat capable of it?",
    ],
    timing: "Days to weeks before departure.",
  },
  {
    id: "weather-window",
    step: 2,
    name: "Weather window",
    summary:
      "Identify the window of acceptable weather for the passage profile — and the window of UN-acceptable weather either side that you need to avoid.",
    inputs: [
      "Synoptic forecasts (T+24, T+48, T+72 + 5-day outlook)",
      "Inshore Waters Forecast + Shipping Forecast for the relevant areas",
      "Boat + crew limits — what wind speed are you happy with?",
    ],
    outputs: [
      "Earliest acceptable departure window",
      "Latest acceptable departure window (after which the weather changes for the worse)",
      "Wind direction + strength forecast for the passage",
      "Sea state forecast + visibility forecast",
    ],
    questions: [
      "Is there a window that fits your tidal + daylight constraints?",
      "When does the window CLOSE?",
      "What's the wind direction + strength expected during the passage?",
      "What FRONT is approaching or passing?",
    ],
    timing: "5 to 1 days before departure; re-checked the day before + the morning of.",
  },
  {
    id: "tidal-gates",
    step: 3,
    name: "Tidal gates + constraints",
    summary:
      "Identify the time windows when you MUST be at specific points to safely transit them — bridges, sills, narrow channels, overfalls, tidal races.",
    inputs: [
      "Tidal predictions for relevant standard ports",
      "Secondary-port corrections if applicable",
      "Tidal stream atlas / diamond data for through-passage segments",
      "Chart of the route with marked hazards / constraints",
    ],
    outputs: [
      "List of tidal gates with their open/close times for the planned date",
      "Departure-time backsolve: when must you LEAVE to arrive at each gate during its window?",
      "Tidal stream profile along the route",
    ],
    questions: [
      "Which gates apply on this route?",
      "What's the earliest you can leave + still hit the first gate?",
      "What's the latest you can leave + still arrive in daylight?",
      "Does the tide work FOR you (stream pushing) or AGAINST you (stream punching)?",
    ],
    timing: "3 to 1 days before departure; tide table reading + arithmetic.",
  },
  {
    id: "route-design",
    step: 4,
    name: "Route design",
    summary:
      "Plot the actual track — waypoints, courses to steer (with variation + deviation), distances, ETAs at each waypoint.",
    inputs: [
      "Charts (paper + electronic) of the area",
      "Tidal stream data (from stage 3)",
      "Compass deviation table for the boat",
      "Expected boat speed under the conditions (planning speed)",
    ],
    outputs: [
      "Waypoint list with lat/lon + bearings + distances between",
      "Course to steer (CTS) for each leg, with compass error applied",
      "ETA at each waypoint",
      "Cross-track tolerance + danger bearings noted for each leg",
    ],
    questions: [
      "Are all waypoints in safe water + clear of hazards?",
      "Are courses entered on the GPS / chartplotter + cross-checked against the paper plot?",
      "Have you allowed for set + drift over each leg?",
    ],
    timing: "2 to 0 days before departure.",
  },
  {
    id: "contingencies",
    step: 5,
    name: "Contingencies + bolt-holes",
    summary:
      "Identify the alternative ports + bail-out options if the weather deteriorates, the boat fails, or someone gets sick.",
    inputs: [
      "List of accessible coastal ports along the route",
      "Knowledge of each port's depth + entrance restrictions",
      "Crew + boat capability",
    ],
    outputs: [
      "Bail-out port list with positions + access conditions (depth, height-of-tide-required, opening hours, marina contact)",
      "Decision triggers: 'IF wind > F7 from W, divert to Brighton; IF sea state > 4, divert to ...'",
      "Communication plan: who you call from the bail-out port + when",
    ],
    questions: [
      "Where would you bail out at each stage of the passage?",
      "What's the access depth at each bail-out port at the time you might arrive?",
      "Have you briefed the crew on the bail-out options?",
    ],
    timing: "1 to 0 days before departure.",
  },
  {
    id: "provisioning",
    step: 6,
    name: "Provisioning",
    summary:
      "Load water, fuel, food, gas + crew kit appropriate for the expected duration + the worst-case delay scenarios.",
    inputs: [
      "Crew count + days at sea (from stage 1)",
      "Standard consumable rates (see @nav-games/predeparture)",
      "Reserve fraction (RYA-suggested 30%)",
    ],
    outputs: [
      "Provisioning manifest: how much of each consumable to load",
      "Stowage plan: where everything lives below decks",
      "Special items: medication, photo ID, ship's papers, cash",
    ],
    questions: [
      "Have you loaded provisions to the planned duration + 30% reserve?",
      "Is the gas bottle full + the spare on board?",
      "Are batteries charged + the engine fully fuelled?",
    ],
    timing: "1 to 0 days before departure.",
  },
  {
    id: "crew-brief",
    step: 7,
    name: "Crew brief + final checks",
    summary:
      "Brief the crew on the passage plan, safety procedures, watch system, emergency drills (see @nav-games/predeparture crew-brief template).",
    inputs: [
      "Passage plan + waypoint list",
      "Weather forecast (final)",
      "Safety equipment + emergency procedures briefing template",
      "Watch system + meal rota",
    ],
    outputs: [
      "Crew aware of route + waypoints + ETA + bail-outs",
      "Crew briefed on lifejacket / harness policy + MOB + fire + abandon-ship",
      "Watch rota agreed + first watch identified",
      "Shore-based emergency contact informed (departure call)",
    ],
    questions: [
      "Does every crew member know what to do in a MOB / fire / abandon ship?",
      "Has the shore contact got the departure time + ETA + 'call CG if I don't hear from you by ...' timing?",
      "Has everyone got into their watch-clothing + lifejacket BEFORE you slip lines?",
    ],
    timing: "Day of departure, in the harbour or marina.",
  },
  {
    id: "departure-arrival",
    step: 8,
    name: "Departure + arrival",
    summary:
      "Execute the plan, monitor + revise, arrive safely + close out the passage with the shore contact.",
    inputs: [
      "All previous stages",
      "Current weather + tide observation",
      "Active passive-watchkeeping",
    ],
    outputs: [
      "Passage executed",
      "Log book up to date",
      "Shore contact informed of arrival",
      "Lessons learned noted in the log",
    ],
    questions: [
      "Are you sailing the plan you made, or has it required a divert?",
      "If divert: have you informed the shore contact + the Coastguard if necessary?",
      "On arrival: has the shore contact been called to release any alert window?",
    ],
    timing: "From slipping lines through to arrival + reporting.",
  },
]);

export const WORKFLOW_IDS: readonly WorkflowStageId[] = WORKFLOW.map(
  (s) => s.id,
);

// ───────────────────────────────────────────────────────────────────
// 2. Tidal gate — type + simple calculator
// ───────────────────────────────────────────────────────────────────

export interface TidalGate {
  /// Identifier (e.g. "Hurst-Narrows", "Portland-Race").
  id: string;
  /// Display name.
  name: string;
  /// Description of the constraint.
  constraint: string;
  /// Window relative to local HW — "HW-3 to HW+1" etc.
  window: string;
  /// Why the window — flood vs ebb, slack water, etc.
  rationale: string;
}

export const COMMON_TIDAL_GATES: readonly TidalGate[] = Object.freeze([
  {
    id: "hurst-narrows",
    name: "Hurst Narrows (Solent)",
    constraint:
      "Strong tidal stream up to 5 kt against you on the ebb; uncomfortable wind-against-tide conditions for the previous 3 hours.",
    window: "HW Portsmouth -3 to HW Portsmouth +3 (with the flood east-going).",
    rationale:
      "Best to transit on the flood (east-going) — stream carries you through. Against the ebb is exhausting + risky in any wind from the east.",
  },
  {
    id: "portland-race",
    name: "Portland Race",
    constraint:
      "Severe overfalls south of Portland Bill at all states except slack water; standing waves up to 4 m in wind against tide.",
    window: "HW Dover +5 to HW Dover -5 (slack water around HW Dover ±1).",
    rationale:
      "Most yachts wait for slack water at the inshore passage close to the Bill. NEVER go through the Race itself — go inshore + take the inside passage with caution.",
  },
  {
    id: "alderney-race",
    name: "Alderney Race / Raz Blanchard",
    constraint:
      "Tidal stream up to 12 kt between Alderney and Cap de la Hague, generating major overfalls.",
    window: "Within 2 hours of slack water (HW St Helier +5 typical for the south-going slack).",
    rationale:
      "One of Europe's fastest tidal streams. Plan to arrive at slack and transit BEFORE the stream gets up.",
  },
  {
    id: "drying-harbour-sill",
    name: "Drying harbour sill",
    constraint:
      "Sill drying at LW; need minimum-clearance depth over the sill to enter.",
    window: "Half-flood to half-ebb — typically HW ±3 hours.",
    rationale:
      "Cumulative tidal height must exceed the boat's required draft + sill height. Rule of twelfths calculation.",
  },
  {
    id: "ic-lifting-bridge",
    name: "Lifting bridge",
    constraint:
      "Bridge lifts at scheduled times only OR requires VHF request.",
    window: "Defined by published timetable — typically every 2 hours OR on request.",
    rationale:
      "Adjust your ETA to arrive at the bridge during a scheduled lift OR with sufficient time to call ahead for a lift on request.",
  },
]);

/// Simple calculator: given the time of HW + a window expressed as
/// hours before/after HW, returns the window start + end times.
export function tidalGateWindow(opts: {
  hwTimeHours: number; // hours since midnight, e.g. 9.5 = 09:30
  hoursBeforeHw: number; // e.g. 3
  hoursAfterHw: number; // e.g. 1
}): { startHours: number; endHours: number } {
  const start = opts.hwTimeHours - opts.hoursBeforeHw;
  const end = opts.hwTimeHours + opts.hoursAfterHw;
  return { startHours: start, endHours: end };
}

/// Backsolve a required departure time. Given the arrival window at
/// a tidal gate + the cruising speed + the distance to the gate, what
/// is the latest acceptable departure?
export function latestDeparture(opts: {
  gateOpenHours: number;
  gateCloseHours: number;
  distanceToGateNm: number;
  cruisingSpeedKt: number;
}): { earliestDep: number; latestDep: number } {
  const transitHours = opts.distanceToGateNm / opts.cruisingSpeedKt;
  return {
    earliestDep: opts.gateOpenHours - transitHours,
    latestDep: opts.gateCloseHours - transitHours,
  };
}

// ───────────────────────────────────────────────────────────────────
// 3. Contingency / bail-out planning
// ───────────────────────────────────────────────────────────────────

export interface ContingencyTrigger {
  id: string;
  /// What you're watching for.
  trigger: string;
  /// Action to take when the trigger fires.
  action: string;
  /// Comms requirement.
  comms: string;
}

export const COMMON_TRIGGERS: readonly ContingencyTrigger[] = Object.freeze([
  {
    id: "weather-deterioration",
    trigger: "Forecast worsens beyond planned limits (wind > F-X, sea > forecast +1 category).",
    action:
      "Divert to nearest bail-out port. Re-evaluate route in shelter before continuing.",
    comms:
      "VHF Pan Pan if you need help; otherwise simply call ahead to the new destination to confirm berth availability + ETA.",
  },
  {
    id: "crew-illness",
    trigger:
      "Crew member becomes incapacitated (severe seasickness, hypothermia, injury) requiring shore-based medical attention.",
    action:
      "Divert to nearest port with medical access. Provide first aid en route per the casualty's condition.",
    comms:
      "VHF Pan Pan if serious + you need medical advice. If life-threatening: Mayday.",
  },
  {
    id: "equipment-failure",
    trigger:
      "Major equipment fails — steering failure, engine failure that can't be repaired underway, hull breach, major sail damage.",
    action:
      "Heave-to to assess if possible. Repair if practical OR divert to a bail-out port under whatever sail / engine remains.",
    comms:
      "VHF Pan Pan if you need a tow or assistance. Mayday if vessel safety is at imminent risk.",
  },
  {
    id: "navigational-doubt",
    trigger:
      "Lost position; GPS failure + uncertain visual fix; weather closes visibility unexpectedly.",
    action:
      "Heave-to / hold position. Establish fix from any available means. If can't, contact Coastguard.",
    comms:
      "VHF call to nearest MRCC for radar plot if you can't establish position by other means. NOT a distress call — a navigation safety call.",
  },
  {
    id: "missed-tidal-gate",
    trigger:
      "Unable to make a tidal gate within its window (delayed start, slower than planned, weather).",
    action:
      "Either continue with adverse stream (acceptable if conditions permit) OR divert to a holding port + wait for the next gate.",
    comms:
      "Inform shore contact of revised ETA. No emergency comms needed unless safety affected.",
  },
]);
